import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Lock } from "lucide-react";
import { LinkedinIcon, XiaohongshuIcon, BilibiliIcon, YoutubeIcon, InstagramIcon } from "./SocialMediaIcons";

const Footer = () => {
  const { t } = useTranslation('footer');

  return (
    <footer className="bg-earth-800 text-white pt-14 pb-6" role="contentinfo" aria-label="Site footer">
      <div className="container mx-auto px-4">
        {/* Main content — centered flex layout */}
        <div className="flex flex-col lg:flex-row justify-center gap-12 lg:gap-16 mb-10">
          {/* Brand column */}
          <div className="flex flex-col items-center lg:items-start text-center lg:text-left max-w-xs">
            <Link to="/" className="inline-block mb-4">
              <h3 className="text-2xl font-brand">Resonow</h3>
            </Link>
            <p className="text-earth-300 text-sm leading-relaxed mb-5">
              {t('tagline')}
            </p>
            <div className="flex items-center gap-3" role="list" aria-label="Social media links">
              <a href="https://www.instagram.com/resonow.official/" className="text-earth-400 hover:text-white transition-colors" target="_blank" rel="noopener noreferrer" aria-label="Instagram" role="listitem">
                <InstagramIcon size={18} />
              </a>
              <a href="https://linkedin.com/company/resonow" className="text-earth-400 hover:text-white transition-colors" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" role="listitem">
                <LinkedinIcon size={18} />
              </a>
              <a href="https://youtube.com/@resonow?si=keiUKb8zxmcHZzP_" className="text-earth-400 hover:text-white transition-colors" target="_blank" rel="noopener noreferrer" aria-label="YouTube" role="listitem">
                <YoutubeIcon size={18} />
              </a>
              <a href="https://www.xiaohongshu.com/user/profile/63e3d3dd0000000027028c96" className="text-earth-400 hover:text-white transition-colors" target="_blank" rel="noopener noreferrer" aria-label="Xiaohongshu" role="listitem">
                <XiaohongshuIcon size={18} />
              </a>
              <a href="https://m.bilibili.com/space/3690981721377158" className="text-earth-400 hover:text-white transition-colors" target="_blank" rel="noopener noreferrer" aria-label="Bilibili" role="listitem">
                <BilibiliIcon size={18} />
              </a>
            </div>
          </div>

          {/* Link columns — evenly spaced */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-10 text-center sm:text-left">
            {/* Explore */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider text-earth-200 mb-4">{t('explore')}</h4>
              <ul className="space-y-2.5 text-sm">
                <li><Link to="/book-an-appointment" className="text-earth-300 hover:text-white transition-colors">{t('findPractitioners')}</Link></li>
                <li><Link to="/events" className="text-earth-300 hover:text-white transition-colors">{t('groupSessions')}</Link></li>
                <li><Link to="/framework" className="text-earth-300 hover:text-white transition-colors">{t('healingFramework')}</Link></li>
                <li><Link to="/booking-choice" className="text-earth-300 hover:text-white transition-colors">{t('bookSession')}</Link></li>
              </ul>
            </div>

            {/* For Practitioners */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider text-earth-200 mb-4">{t('forPractitioners')}</h4>
              <ul className="space-y-2.5 text-sm">
                <li><Link to="/become-practitioner" className="text-earth-300 hover:text-white transition-colors">{t('joinPlatform')}</Link></li>
                <li><Link to="/practitioner-auth" className="text-earth-300 hover:text-white transition-colors">{t('practitionerLogin')}</Link></li>
              </ul>
            </div>

            {/* Company */}
            <div className="col-span-2 sm:col-span-1">
              <h4 className="text-sm font-semibold uppercase tracking-wider text-earth-200 mb-4">{t('company')}</h4>
              <ul className="space-y-2.5 text-sm">
                <li><Link to="/about-us" className="text-earth-300 hover:text-white transition-colors">{t('aboutUs')}</Link></li>
                <li><Link to="/careers" className="text-earth-300 hover:text-white transition-colors">{t('careers')}</Link></li>
                <li><Link to="/news-events" className="text-earth-300 hover:text-white transition-colors">{t('newsEvents')}</Link></li>
                <li><Link to="/crisis-resources" className="text-earth-300 hover:text-white transition-colors">{t('crisisResources')}</Link></li>
                <li><Link to="/accessibility" className="text-earth-300 hover:text-white transition-colors">{t('accessibility')}</Link></li>
              </ul>
            </div>
          </div>
        </div>

        {/* Compliance badges */}
        <div className="border-t border-earth-700 pt-6">
          <div className="flex flex-wrap justify-center gap-2.5 mb-5">
            {[
              { icon: ShieldCheck, label: t('gdprCompliant') },
              { icon: ShieldCheck, label: t('ukGdprCompliant') },
              { icon: ShieldCheck, label: t('pdpaCompliant') },
              { icon: ShieldCheck, label: t('ccpaCompliant') },
              { icon: Lock, label: t('encrypted') },
            ].map(({ icon: Icon, label }) => (
              <Link
                key={label}
                to="/privacy-policy"
                className="inline-flex items-center gap-1.5 rounded-full border border-earth-600 px-3 py-1 text-xs text-earth-400 hover:text-earth-300 hover:border-earth-500 transition-colors"
              >
                <Icon className="w-3 h-3" />
                {label}
              </Link>
            ))}
          </div>

          {/* Sub-footer — horizontal */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-xs text-earth-400 text-center">
            <p>&copy; {new Date().getFullYear()} Resonow. {t('copyright')}</p>
            
            <div className="flex gap-4 shrink-0">
              <Link to="/privacy-policy" className="hover:text-earth-300 transition-colors">{t('privacyPolicy')}</Link>
              <Link to="/terms-of-service" className="hover:text-earth-300 transition-colors">{t('termsOfService')}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;